import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import {
  AI_AGENT_CONNECTION_ENDPOINT,
  AI_AGENT_CONNECTION_MODELS_ENDPOINT,
  AI_AGENT_CONNECTION_PAYLOAD_TEST_ENDPOINT,
  AI_AGENT_CONNECTION_STATUS_ENDPOINT,
  AI_AGENT_CONNECTION_TEST_ENDPOINT,
  AI_AGENT_AVAILABLE_CONNECTIONS_ENDPOINT,
  AI_AGENT_CONVERSATION_ENDPOINT,
  AI_AGENT_CONVERSATIONS_ENDPOINT,
  AI_AGENT_START_CONVERSATION_ENDPOINT,
  AI_AGENT_CONVERSATION_GROUPS_ENDPOINT,
  AI_AGENT_CONVERSATION_GROUP_ENDPOINT,
  AI_AGENT_MESSAGES_ENDPOINT,
  AI_AGENT_ORGANISATION_CONNECTIONS_ENDPOINT,
  AI_AGENT_PERSONAL_CONNECTIONS_ENDPOINT,
  AI_AGENT_PROVIDERS_ENDPOINT,
} from '../api/api-endpoints';
import { RestApiService } from '../api/rest-api.service';
import { ReportOutputKind, ReportQuery, ReportVisualization } from './report-query';

export type AiConnectionScope = 'personal' | 'organisation';

export interface AiProvider {
  id: string;
  label: string;
  default_url: string;
  endpoint_required: boolean;
  api_key_required: boolean;
  region_required: boolean;
  project_id_required: boolean;
  credential_url: string;
}

export interface AiModelConnection {
  id: string;
  scope: AiConnectionScope;
  name: string;
  provider: string;
  base_url: string;
  region: string | null;
  project_id: string | null;
  priority: number;
  active: boolean;
  max_output_tokens: number;
  has_credentials: boolean;
  last_tested_at: string | null;
  last_test_succeeded: boolean | null;
  created_at: string;
  updated_at: string;
}

export interface AiModelConnectionPayload {
  name: string;
  provider: string;
  base_url: string;
  api_key: string | null;
  region: string | null;
  project_id: string | null;
  priority: number;
  active: boolean;
  max_output_tokens: number;
}

export interface AiConversation {
  id: string;
  title: string;
  organisation_id: string | null;
  group_id: string | null;
  pinned_at: string | null;
  is_running: boolean;
  pending?: boolean;
  created_at: string;
  updated_at: string;
}

export interface AiConversationPage {
  items: AiConversation[];
  page: number;
  page_size: number;
  has_more: boolean;
}

export interface AiConversationGroup {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface AiConnectionStatus {
  connected: boolean;
  scope: AiConnectionScope | null;
  name: string | null;
}

export interface AiConnectionModels {
  connection_id: string;
  models: string[];
}

export interface AiWidget {
  template: 'report-widget';
  title: string;
  output: ReportOutputKind;
  query: ReportQuery;
  visualization?: ReportVisualization;
}

export interface AiMessage {
  id: string;
  conversation_id: string;
  client_request_id: string;
  role: 'user' | 'assistant';
  content: string;
  status: 'pending' | 'complete' | 'failed';
  model_label: string | null;
  widgets: AiWidget[];
  queries: ReportQuery[];
  created_at: string;
  updated_at: string;
}

@Injectable({ providedIn: 'root' })
export class AiAgentsService {
  constructor(private readonly api: RestApiService) {}

  providers(): Observable<AiProvider[]> { return this.api.get(AI_AGENT_PROVIDERS_ENDPOINT); }
  connectionStatus(): Observable<AiConnectionStatus> { return this.api.get(AI_AGENT_CONNECTION_STATUS_ENDPOINT); }

  listConnections(scope: AiConnectionScope): Observable<AiModelConnection[]> {
    return this.api.get(scope === 'personal' ? AI_AGENT_PERSONAL_CONNECTIONS_ENDPOINT : AI_AGENT_ORGANISATION_CONNECTIONS_ENDPOINT);
  }

  createConnection(scope: AiConnectionScope, payload: AiModelConnectionPayload): Observable<AiModelConnection> {
    return this.api.post(scope === 'personal' ? AI_AGENT_PERSONAL_CONNECTIONS_ENDPOINT : AI_AGENT_ORGANISATION_CONNECTIONS_ENDPOINT, payload);
  }

  updateConnection(scope: AiConnectionScope, id: string, payload: AiModelConnectionPayload): Observable<AiModelConnection> {
    return this.api.put(AI_AGENT_CONNECTION_ENDPOINT(scope, id), payload);
  }

  deleteConnection(scope: AiConnectionScope, id: string): Observable<void> {
    return this.api.delete(AI_AGENT_CONNECTION_ENDPOINT(scope, id));
  }

  testConnection(scope: AiConnectionScope, id: string): Observable<{ connected: boolean; message: string }> {
    return this.api.post(AI_AGENT_CONNECTION_TEST_ENDPOINT(scope, id), {});
  }

  testConnectionPayload(scope: AiConnectionScope, payload: AiModelConnectionPayload): Observable<{ connected: boolean; message: string }> {
    return this.api.post(AI_AGENT_CONNECTION_PAYLOAD_TEST_ENDPOINT(scope), payload);
  }

  availableConnections(): Observable<AiModelConnection[]> {
    return this.api.get(AI_AGENT_AVAILABLE_CONNECTIONS_ENDPOINT);
  }

  connectionModels(id: string): Observable<AiConnectionModels> {
    return this.api.get(AI_AGENT_CONNECTION_MODELS_ENDPOINT(id));
  }

  listConversations(section: 'recent' | 'pinned' | 'group' = 'recent', page = 1, limit = 20, groupId?: string): Observable<AiConversationPage> {
    return this.api.get(AI_AGENT_CONVERSATIONS_ENDPOINT, { params: { section, page, limit, ...(groupId ? { group_id: groupId } : {}) } });
  }
  startConversation(content: string, clientRequestId: string, connectionId?: string, model?: string): Observable<{ conversation: AiConversation; message: AiMessage }> {
    return this.api.post(AI_AGENT_START_CONVERSATION_ENDPOINT, this.turnPayload(content, clientRequestId, connectionId, model));
  }
  getConversation(id: string): Observable<AiConversation> { return this.api.get(AI_AGENT_CONVERSATION_ENDPOINT(id)); }
  updateConversation(id: string, payload: { title?: string; pinned?: boolean; group_id?: string; remove_from_group?: boolean }): Observable<AiConversation> {
    return this.api.patch(AI_AGENT_CONVERSATION_ENDPOINT(id), payload);
  }
  deleteConversation(id: string): Observable<void> { return this.api.delete(AI_AGENT_CONVERSATION_ENDPOINT(id)); }
  listConversationGroups(): Observable<AiConversationGroup[]> { return this.api.get(AI_AGENT_CONVERSATION_GROUPS_ENDPOINT); }
  createConversationGroup(name: string): Observable<AiConversationGroup> { return this.api.post(AI_AGENT_CONVERSATION_GROUPS_ENDPOINT, { name }); }
  updateConversationGroup(id: string, name: string): Observable<AiConversationGroup> { return this.api.patch(AI_AGENT_CONVERSATION_GROUP_ENDPOINT(id), { name }); }
  deleteConversationGroup(id: string): Observable<void> { return this.api.delete(AI_AGENT_CONVERSATION_GROUP_ENDPOINT(id)); }
  listMessages(id: string): Observable<AiMessage[]> { return this.api.get(AI_AGENT_MESSAGES_ENDPOINT(id)); }
  send(id: string, content: string, clientRequestId: string, connectionId?: string, model?: string): Observable<AiMessage> {
    return this.api.post(AI_AGENT_MESSAGES_ENDPOINT(id), this.turnPayload(content, clientRequestId, connectionId, model));
  }

  private turnPayload(content: string, clientRequestId: string, connectionId?: string, model?: string) {
    return { content, client_request_id: clientRequestId, connection_id: connectionId ?? null, model: model ?? null };
  }
}
